import {
  Box,
  Button,
  Flex,
  Icon,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  getExpandedRowModel
} from '@tanstack/react-table';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdExpandMore, MdChevronRight as MdExpandLess } from 'react-icons/md';
import InnerTable from './InnerTable';
import { useNavigate } from 'react-router-dom';

const Pagination = ({ table }) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  const totalRows = table.getRowCount();

  const start = pageIndex * pageSize + 1;
  const end = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <Flex
      direction={{ sm: 'column', md: 'row' }}
      justify="space-between"
      align={{ sm: 'start', md: 'center' }}
      w={'100%'}
      px={{ md: '22px' }}
      fontSize="sm"
      color="gray.500"
      fontWeight="normal"
    >
      <Flex align="center">
        <Text me="10px" minW="max-content">
          Show rows per page
        </Text>
        <Select
          fontSize="sm"
          variant="main"
          value={pageSize}
          onChange={(e) => {
            let _pageSize = parseInt(e.target.value);
            if (_pageSize === -1) {
              _pageSize = totalRows;
            }
            table.setPageSize(_pageSize);
          }}
        >
          <option value="7">7</option>
          <option value="21">21</option>
          <option value="-1">All</option>
        </Select>
      </Flex>
      <Flex align={'center'} gap={5}>
        <Text>{`${start} - ${end} of ${totalRows}`}</Text>
        <Button
          variant="no-effects"
          p={0}
          minW={'max'}
          onClick={() => table.previousPage()}
          isDisabled={!table.getCanPreviousPage()}
        >
          <Icon as={MdChevronLeft} boxSize={5} />
        </Button>
        <Button
          variant="no-effects"
          p={0}
          minW={'max'}
          onClick={() => table.nextPage()}
          isDisabled={!table.getCanNextPage()}
        >
          <Icon as={MdChevronRight} boxSize={5} />
        </Button>
      </Flex>
    </Flex>
  );
};

function CollapsableDataTable({
  data,
  columns,
  defaultSorting,
  hasPagination,
  onRowClick,
  onNestedRowClick,
  rowHeight,
  nestedRowDataSelector,
  nestedRowRenderer,
  customerId
}) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverColor = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 7 });
  const [nestedRowData, setNestedRowData] = useState({});
  const navigate = useNavigate();

  const getNestedRowData = (data, row) => {
    const dataRow = data.find(item => item.id === row.original.id);
    const computed = dataRow ? (nestedRowDataSelector(dataRow) ?? []) : [];
    if (!nestedRowData[row.id]) {
      setNestedRowData(prev => ({ ...prev, [row.id]: computed }));
      return computed;
    }
    return nestedRowData[row.id];
  };

  const toggleRow = (rowId) => {
    setExpanded(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getPaginationRowModel: hasPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getSubRows: row => row.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      pagination,
      expanded
    },
  });

  useEffect(() => {
    if (!defaultSorting?.length) {
      setSorting([]);
      return;
    }
    const columnIds = new Set(
      columns.map((col) => col.id ?? col.accessorKey).filter(Boolean)
    );
    const validSorting = defaultSorting.filter((s) => columnIds.has(s.id));
    setSorting(validSorting);
  }, [columns, defaultSorting]);

  return (
    <Flex direction="column" w="100%">
      <Box overflowX={'auto'}>
        <Table variant={'simple'} mb={6}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                <Th
                  borderColor={borderColor}
                  w="50px"
                  textAlign="center"
                >
                  <Text fontSize={{ sm: '12px', lg: '14px' }} color="gray.400">
                    
                  </Text>
                </Th>
                {headerGroup.headers.map((header) => {
                  const column = header.column;
                  return (
                    <Th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      cursor={column.getCanSort() ? 'pointer' : 'default'}
                      pe="10px"
                      borderColor={borderColor}
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: '12px', lg: '14px' }}
                        color="gray.400"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <Tr
                  h={rowHeight || 'auto'}
                  cursor="default"
                  _hover={{
                    bg: hoverColor,
                  }}
                >
                  <Td
                    borderColor={borderColor}
                    w="50px"
                    textAlign="center"
                  >
                    <Icon
                      as={expanded[row.id] ? MdExpandMore : MdExpandLess}
                      boxSize={5}
                      cursor="pointer"
                      onClick={() => toggleRow(row.id)}
                      transform={expanded[row.id] ? 'rotate(0deg)' : 'rotate(-90deg)'}
                      transition="transform 0.2s"
                      color="gray.500"
                      _hover={{ color: 'blue.500' }}
                    />
                  </Td>
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    const { maxWidth, align } = meta || {};
                    return (
                      <Td
                        textAlign={align}
                        key={cell.id}
                        maxW={maxWidth}
                        wordBreak={'keep-all'}
                        fontSize={{ sm: '16px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor={borderColor}
                        onClick={onRowClick && (() => onRowClick(row.original))}
                        cursor={!!onRowClick ? 'pointer' : 'default'}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    );
                  })}
                </Tr>
                {expanded[row.id] && (
                  <Tr>
                    <Td colSpan={row.getVisibleCells().length + 1} p={0} borderColor={borderColor}>
                      <InnerTable
                          data={getNestedRowData(data, row)}
                          onRowClick={onNestedRowClick}
                          rowRenderer = {nestedRowRenderer}
                      />
                    </Td>
                  </Tr>
                )}
              </Fragment>
            ))}
          </Tbody>
        </Table>
      </Box>
      {hasPagination && <Pagination table={table} />}
    </Flex>
  );
}

CollapsableDataTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  hasPagination: PropTypes.bool,
  rowHeight: PropTypes.string,
  onRowClick: PropTypes.func,
  onNestedRowClick: PropTypes.func,
  nestedRowDataSelector: PropTypes.func,
  nestedRowRenderer: PropTypes.func,
  defaultSorting: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      desc: PropTypes.bool.isRequired,
    })
  ),
  customerId: PropTypes.string,
};

export default CollapsableDataTable;
